import React, { useState, useRef, useEffect } from 'react'

export interface NavItem {
  label: string
  id: string
  icon?: React.ReactNode
  onClick?: () => void
}

interface NavigationPillProps {
  items: NavItem[]
  activeId?: string
  onItemClick?: (id: string) => void
  collapsedWidth?: number
  expandedWidth?: number
  collapsedHeight?: number
  expandedHeight?: number
  className?: string
  orientation?: 'horizontal' | 'vertical'
}

/**
 * 3D Adaptive Navigation Pill
 * CSS-only animations (no framer-motion) for ~80KB bundle savings.
 * Same visual identity, hover-to-expand behavior preserved.
 */
export const NavigationPill: React.FC<NavigationPillProps> = ({
  items,
  activeId,
  onItemClick,
  collapsedWidth = 140,
  expandedWidth,
  collapsedHeight = 56,
  expandedHeight,
  className = '',
  orientation = 'horizontal',
}) => {
  const [activeSection, setActiveSection] = useState(activeId || items[0]?.id || '')
  const [expanded, setExpanded] = useState(false)
  const [hovering, setHovering] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isVertical = orientation === 'vertical'

  const calculatedExpandedWidth = expandedWidth || (isVertical ? collapsedWidth : Math.max(items.length * 130, 400))
  const calculatedExpandedHeight = expandedHeight || (isVertical ? Math.max(items.length * 48, 200) : collapsedHeight)

  useEffect(() => {
    if (activeId) setActiveSection(activeId)
  }, [activeId])

  useEffect(() => {
    if (hovering) {
      setExpanded(true)
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current)
    } else {
      hoverTimeoutRef.current = setTimeout(() => setExpanded(false), 600)
    }
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current)
    }
  }, [hovering])

  const handleSectionClick = (item: NavItem) => {
    setIsTransitioning(true)
    setActiveSection(item.id)
    setHovering(false)
    item.onClick?.()
    onItemClick?.(item.id)
    setTimeout(() => setIsTransitioning(false), 400)
  }

  const activeItem = items.find(item => item.id === activeSection)
  const borderRadius = isVertical ? '1.5rem' : '9999px'

  const width = isVertical ? collapsedWidth : (expanded ? calculatedExpandedWidth : collapsedWidth)
  const height = isVertical ? (expanded ? calculatedExpandedHeight : collapsedHeight) : collapsedHeight

  const baseShadow = `
    0 3px 6px hsl(var(--foreground) / 0.12),
    0 8px 16px hsl(var(--foreground) / 0.10),
    0 16px 32px hsl(var(--foreground) / 0.08),
    0 1px 2px hsl(var(--foreground) / 0.12),
    inset 0 2px 1px hsl(var(--background) / 0.7),
    inset 0 -2px 6px hsl(var(--foreground) / 0.10),
    inset 2px 2px 8px hsl(var(--foreground) / 0.08),
    inset -2px 2px 8px hsl(var(--foreground) / 0.07),
    inset 0 0 1px hsl(var(--foreground) / 0.15)
  `
  const expandedShadow = `
    0 2px 4px hsl(var(--foreground) / 0.08),
    0 6px 12px hsl(var(--foreground) / 0.12),
    0 12px 24px hsl(var(--foreground) / 0.14),
    0 24px 48px hsl(var(--foreground) / 0.10),
    inset 0 2px 2px hsl(var(--background) / 0.8),
    inset 0 -3px 8px hsl(var(--foreground) / 0.12),
    inset 3px 3px 8px hsl(var(--foreground) / 0.10),
    inset -3px 3px 8px hsl(var(--foreground) / 0.09),
    inset 0 -1px 2px hsl(var(--foreground) / 0.08)
  `
  const transitioningShadow = `
    0 3px 6px hsl(var(--foreground) / 0.10),
    0 8px 16px hsl(var(--foreground) / 0.08),
    0 16px 32px hsl(var(--foreground) / 0.06),
    inset 0 2px 1px hsl(var(--background) / 0.85),
    inset 0 -2px 6px hsl(var(--foreground) / 0.08),
    inset 2px 2px 8px hsl(var(--foreground) / 0.06),
    inset -2px 2px 8px hsl(var(--foreground) / 0.05),
    inset 0 0 1px hsl(var(--foreground) / 0.12),
    inset 0 0 20px hsl(var(--background) / 0.15)
  `

  return (
    <nav
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      className={`relative ${className}`}
      style={{
        width,
        height,
        borderRadius,
        background: 'linear-gradient(135deg, hsl(var(--card)) 0%, hsl(var(--muted)) 50%, hsl(var(--card)) 100%)',
        boxShadow: expanded ? expandedShadow : isTransitioning ? transitioningShadow : baseShadow,
        overflow: 'hidden',
        // Spring-like easing (overshoot, ~stiffness:220 / damping:25)
        transition: 'width 480ms cubic-bezier(0.34, 1.56, 0.64, 1), height 480ms cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 360ms ease-out',
      }}
    >
      {/* Decorative layers (unchanged visual) */}
      <div
        className={`absolute pointer-events-none ${isVertical ? 'inset-y-0 left-0' : 'inset-x-0 top-0'}`}
        style={{
          width: isVertical ? '2px' : undefined,
          height: isVertical ? undefined : '2px',
          borderRadius: isVertical ? '1.5rem 0 0 1.5rem' : '9999px 9999px 0 0',
          background: isVertical
            ? 'linear-gradient(180deg, transparent 0%, hsl(var(--background) / 0.95) 5%, hsl(var(--background)) 15%, hsl(var(--background)) 85%, hsl(var(--background) / 0.95) 95%, transparent 100%)'
            : 'linear-gradient(90deg, transparent 0%, hsl(var(--background) / 0.95) 5%, hsl(var(--background)) 15%, hsl(var(--background)) 85%, hsl(var(--background) / 0.95) 95%, transparent 100%)',
          filter: 'blur(0.3px)',
        }}
      />
      <div
        className={`absolute pointer-events-none ${isVertical ? 'inset-y-0 left-0' : 'inset-x-0 top-0'}`}
        style={{
          width: isVertical ? '55%' : undefined,
          height: isVertical ? undefined : '55%',
          borderRadius,
          background: isVertical
            ? 'linear-gradient(90deg, hsl(var(--background) / 0.45) 0%, hsl(var(--background) / 0.25) 30%, hsl(var(--background) / 0.10) 60%, transparent 100%)'
            : 'linear-gradient(180deg, hsl(var(--background) / 0.45) 0%, hsl(var(--background) / 0.25) 30%, hsl(var(--background) / 0.10) 60%, transparent 100%)',
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          borderRadius,
          background: 'linear-gradient(135deg, hsl(var(--background) / 0.40) 0%, hsl(var(--background) / 0.20) 20%, hsl(var(--background) / 0.08) 40%, transparent 65%)',
        }}
      />
      {!isVertical && (
        <div
          className="absolute rounded-full pointer-events-none"
          style={{
            left: expanded ? '18%' : '15%',
            top: '16%',
            width: expanded ? '140px' : '60px',
            height: '14px',
            background: 'radial-gradient(ellipse at center, hsl(var(--background) / 0.70) 0%, hsl(var(--background) / 0.35) 40%, hsl(var(--background) / 0.10) 70%, transparent 100%)',
            filter: 'blur(4px)',
            transform: 'rotate(-12deg)',
            transition: 'left 300ms ease, width 300ms ease',
          }}
        />
      )}
      {expanded && !isVertical && (
        <div
          className="absolute rounded-full pointer-events-none"
          style={{
            right: '22%',
            top: '20%',
            width: '80px',
            height: '10px',
            background: 'radial-gradient(ellipse at center, hsl(var(--background) / 0.50) 0%, hsl(var(--background) / 0.15) 60%, transparent 100%)',
            filter: 'blur(3px)',
            transform: 'rotate(8deg)',
          }}
        />
      )}
      <div
        className={`absolute pointer-events-none ${isVertical ? 'inset-y-0 right-0' : 'inset-x-0 bottom-0'}`}
        style={{
          width: isVertical ? '50%' : undefined,
          height: isVertical ? undefined : '50%',
          borderRadius,
          background: isVertical
            ? 'linear-gradient(270deg, hsl(var(--foreground) / 0.14) 0%, hsl(var(--foreground) / 0.08) 25%, hsl(var(--foreground) / 0.03) 50%, transparent 100%)'
            : 'linear-gradient(0deg, hsl(var(--foreground) / 0.14) 0%, hsl(var(--foreground) / 0.08) 25%, hsl(var(--foreground) / 0.03) 50%, transparent 100%)',
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          borderRadius,
          boxShadow: 'inset 0 0 40px hsl(var(--background) / 0.22)',
          opacity: 0.7,
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          borderRadius,
          boxShadow: 'inset 0 0 0 0.5px hsl(var(--foreground) / 0.10)',
        }}
      />

      {/* Items */}
      <div
        ref={containerRef}
        className={`relative z-10 h-full flex items-center justify-center ${isVertical ? 'flex-col py-2 px-2' : 'px-6'}`}
      >
        {!expanded && activeItem && (
          <div
            key={activeItem.id}
            className={`text-foreground font-semibold whitespace-nowrap flex items-center gap-2 ${isVertical ? 'flex-col text-center' : ''}`}
            style={{
              fontSize: isVertical ? '12px' : '15.5px',
              letterSpacing: '0.45px',
              animation: 'pill-fade-in 360ms cubic-bezier(0.34, 1.56, 0.64, 1) both',
            }}
          >
            {activeItem.icon}
            <span className={isVertical ? 'text-[10px]' : ''}>{activeItem.label}</span>
          </div>
        )}

        {expanded && (
          <div className={`flex items-center w-full ${isVertical ? 'flex-col gap-1' : 'justify-evenly'}`}>
            {items.map((item, index) => {
              const isActive = item.id === activeSection
              return (
                <button
                  type="button"
                  key={item.id}
                  aria-label={item.label}
                  aria-current={isActive ? 'page' : undefined}
                  onClick={() => handleSectionClick(item)}
                  className={`relative cursor-pointer transition-all duration-200 bg-transparent border-none outline-none whitespace-nowrap flex items-center gap-2 focus-visible:ring-2 focus-visible:ring-primary rounded-md ${
                    isVertical ? 'w-full justify-start px-3 py-2 rounded-xl' : 'px-4 py-2.5'
                  } ${
                    isActive
                      ? 'text-foreground' + (isVertical ? ' bg-primary/10' : '')
                      : 'text-muted-foreground hover:text-foreground' + (isVertical ? ' hover:bg-muted/50' : '')
                  }`}
                  style={{
                    fontSize: isVertical ? (isActive ? '13px' : '12px') : (isActive ? '15.5px' : '15px'),
                    fontWeight: isActive ? 600 : 500,
                    letterSpacing: '0.45px',
                    transform: !isVertical && isActive ? 'translateY(-1.5px)' : 'translateY(0)',
                    animation: `pill-item-in 320ms cubic-bezier(0.34, 1.56, 0.64, 1) both`,
                    animationDelay: `${60 + index * 55}ms`,
                  }}
                >
                  {item.icon}
                  {item.label}
                </button>
              )
            })}
          </div>
        )}
      </div>

      <style>{`
        @keyframes pill-fade-in {
          0% { opacity: 0; transform: translateY(8px) scale(0.94); filter: blur(4px); }
          60% { opacity: 1; filter: blur(0); }
          100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        @keyframes pill-item-in {
          0% { opacity: 0; transform: translateX(-12px) scale(0.9); }
          60% { opacity: 1; }
          100% { opacity: 1; transform: translateX(0) scale(1); }
        }
      `}</style>
    </nav>
  )
}

export const PillBase = NavigationPill
